import React, { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X, ArrowUp, ArrowDown, ArrowsMerge, Info, Check } from "@phosphor-icons/react";
import { formatApiError } from "@/contexts/AuthContext";

const empty = () => ({ key: "", label: "", subcategories: [], sort_order: 0, active: true });

function slugify(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function CategoryModal({ category, onClose, onSaved }) {
  const [form, setForm] = useState(category ? { ...empty(), ...category } : empty());
  const [subInput, setSubInput] = useState("");
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addSub = () => {
    const s = slugify(subInput);
    if (!s) return;
    if (form.subcategories.includes(s)) { toast.error("Already exists"); return; }
    upd("subcategories", [...form.subcategories, s]);
    setSubInput("");
  };

  const move = (i, dir) => {
    const arr = [...form.subcategories];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    upd("subcategories", arr);
  };

  const removeSub = (i) => upd("subcategories", form.subcategories.filter((_, k) => k !== i));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        key: slugify(form.key),
        sort_order: parseInt(form.sort_order) || 0,
      };
      if (category?.id) {
        // key is immutable — send only editable fields
        const { key: _k, ...editable } = payload;
        await api.put(`/admin/categories/${category.id}`, editable);
        toast.success("Category updated");
      } else {
        if (!payload.key) { toast.error("Key is required"); setSaving(false); return; }
        if (!payload.label) { toast.error("Label is required"); setSaving(false); return; }
        await api.post("/admin/categories", payload);
        toast.success("Category created");
      }
      onSaved(); onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="category-modal">
      <div className="bg-white w-full max-w-xl my-8 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex items-center justify-between p-6 border-b border-neutral-200 z-10">
          <h2 className="font-display font-black text-2xl">{category ? "Edit Category" : "New Category"}</h2>
          <button onClick={onClose} data-testid="category-modal-close" className="hover:text-[color:var(--vt-orange)]"><X size={22} /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <div>
            <label className="overline text-neutral-500 block mb-1">Key* {category && <span className="text-neutral-400 normal-case">(read-only)</span>}</label>
            <input required disabled={!!category} className="vt-input" value={form.key} placeholder="new-bikes"
              onChange={(e) => upd("key", e.target.value)} data-testid="category-key" />
            <p className="text-[11px] text-neutral-500 mt-1">URL-safe slug used on products &amp; storefront links.</p>
          </div>
          <div>
            <label className="overline text-neutral-500 block mb-1">Label*</label>
            <input required className="vt-input" value={form.label} placeholder="New Bikes"
              onChange={(e) => upd("label", e.target.value)} data-testid="category-label" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline text-neutral-500 block mb-1">Sort order</label>
              <input type="number" className="vt-input" value={form.sort_order}
                onChange={(e) => upd("sort_order", e.target.value)} data-testid="category-sort" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.active} onChange={(e) => upd("active", e.target.checked)} data-testid="category-active" /> Active (visible on storefront)
              </label>
            </div>
          </div>

          <div>
            <label className="overline text-neutral-500 block mb-1">Subcategories ({form.subcategories.length})</label>
            <div className="flex gap-2 mb-3">
              <input className="vt-input" value={subInput} placeholder="e.g. mountain, e-mtb"
                onChange={(e) => setSubInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSub(); } }}
                data-testid="subcategory-input" />
              <button type="button" onClick={addSub} className="vt-btn-orange text-xs whitespace-nowrap" data-testid="subcategory-add">+ Add</button>
            </div>
            {form.subcategories.length === 0 ? (
              <p className="text-xs text-neutral-400 italic">No subcategories yet. Add one above.</p>
            ) : (
              <ul className="space-y-1">
                {form.subcategories.map((s, i) => (
                  <li key={s} className="flex items-center gap-2 border border-neutral-200 px-3 py-2 text-sm" data-testid={`subcategory-row-${s}`}>
                    <span className="flex-1 font-mono">{s}</span>
                    <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="p-1 disabled:opacity-30" title="Up"><ArrowUp size={12} /></button>
                    <button type="button" disabled={i === form.subcategories.length - 1} onClick={() => move(i, 1)} className="p-1 disabled:opacity-30" title="Down"><ArrowDown size={12} /></button>
                    <button type="button" onClick={() => removeSub(i)} className="p-1 text-red-600" title="Remove" data-testid={`subcategory-remove-${s}`}><Trash size={12} /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="pt-4 flex gap-2 justify-end sticky bottom-0 bg-white border-t border-neutral-200 -mx-6 px-6 py-4">
            <button type="button" onClick={onClose} className="vt-btn-outline text-xs">Cancel</button>
            <button type="submit" disabled={saving} className="vt-btn-orange text-xs" data-testid="category-save">{saving ? "..." : "Save Category"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminCategories() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [merging, setMerging] = useState(null); // {source: category}
  const [addingSubTo, setAddingSubTo] = useState(null); // category.id
  const [subDraft, setSubDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/categories").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Inline add — writes straight to an existing main category's `subcategories`
  // list via PUT. Cannot create a new main category by accident.
  const addSubInline = async (cat) => {
    const clean = slugify(subDraft);
    if (!clean) { toast.error("Type a subcategory name first"); return; }
    if ((cat.subcategories || []).includes(clean)) {
      toast.error("Already exists on this category"); return;
    }
    setBusy(true);
    try {
      await api.put(`/admin/categories/${cat.id}`, {
        subcategories: [...(cat.subcategories || []), clean],
      });
      toast.success(`Added "${clean}" under ${cat.label}`);
      setSubDraft("");
      setAddingSubTo(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to add subcategory");
    } finally { setBusy(false); }
  };

  const removeSubInline = async (cat, sub) => {
    if (!window.confirm(`Remove subcategory "${sub}" from ${cat.label}?\n\nProducts currently using it keep their assignment — you'll just lose the storefront filter chip.`)) return;
    setBusy(true);
    try {
      await api.put(`/admin/categories/${cat.id}`, {
        subcategories: (cat.subcategories || []).filter((x) => x !== sub),
      });
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to remove");
    } finally { setBusy(false); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete category "${c.label}"?`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      const detail = formatApiError(err.response?.data?.detail) || "Delete failed";
      // If blocked because products use it, offer to merge instead
      if (err.response?.status === 409) {
        if (window.confirm(`${detail}\n\nWould you like to MERGE it into another category instead?`)) {
          setMerging({ source: c });
          return;
        }
      }
      toast.error(detail);
    }
  };

  return (
    <div className="p-8 lg:p-12" data-testid="admin-categories">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="overline text-[color:var(--vt-orange)] mb-2">/ CATALOG</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Categories</h1>
          <p className="text-neutral-500 text-sm mt-1">{items.length} main categor{items.length === 1 ? "y" : "ies"} · click <strong>+ Add Sub</strong> on any row to add a subcategory inside it.</p>
        </div>
        <button onClick={() => setShowNew(true)} className="vt-btn-orange text-xs" data-testid="btn-new-category">
          <Plus size={14} weight="bold" /> New Main Category
        </button>
      </div>

      <div className="mb-6 bg-blue-50 border border-blue-200 p-4 flex gap-3 items-start text-xs text-blue-900" data-testid="cats-explainer">
        <Info size={20} weight="fill" className="flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-bold mb-1">Two levels: MAIN category → subcategories</p>
          <p>
            The <strong>New Main Category</strong> button (top-right) creates top-level buckets like <em>Parts</em>, <em>Accessories</em>.<br />
            To add a subcategory (e.g. <em>Pedals</em>) <strong>inside an existing main category</strong>, click
            <span className="mx-1 px-1.5 py-0.5 bg-white border border-blue-300 font-mono">+ Add Sub</span>
            on that category's row below — it will never create a new main category.
          </p>
        </div>
      </div>

      <div className="bg-white border border-neutral-200 overflow-x-auto">
        {loading ? (
          <div className="p-12 text-center text-neutral-500">Loading...</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-neutral-500" data-testid="no-categories">
            No categories yet. Create your first category to power Product forms &amp; Shop filters.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="text-left p-3 overline text-neutral-600">Key</th>
                <th className="text-left p-3 overline text-neutral-600">Label</th>
                <th className="text-left p-3 overline text-neutral-600">Subcategories</th>
                <th className="text-right p-3 overline text-neutral-600">Sort</th>
                <th className="text-right p-3 overline text-neutral-600">Status</th>
                <th className="text-right p-3 overline text-neutral-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-b border-neutral-100 hover:bg-neutral-50 align-top" data-testid={`category-row-${c.key}`}>
                  <td className="p-3 font-mono text-xs pt-4">{c.key}</td>
                  <td className="p-3 font-bold pt-4">{c.label}</td>
                  <td className="p-3 text-xs text-neutral-600">
                    {(c.subcategories || []).length === 0 ? (
                      <span className="text-neutral-400 italic">no subcategories yet</span>
                    ) : (
                      <div className="flex flex-wrap gap-1.5 mb-1.5">
                        {c.subcategories.map((s) => (
                          <span key={s} className="inline-flex items-center gap-1 bg-neutral-100 pl-2 pr-1 py-0.5 rounded text-[11px] group" data-testid={`sub-chip-${c.key}-${s}`}>
                            <span>{s}</span>
                            <button
                              type="button"
                              onClick={() => removeSubInline(c, s)}
                              disabled={busy}
                              className="opacity-40 group-hover:opacity-100 hover:text-red-600 transition-opacity"
                              title={`Remove ${s} from ${c.label}`}
                              data-testid={`remove-sub-${c.key}-${s}`}
                            ><X size={10} weight="bold" /></button>
                          </span>
                        ))}
                      </div>
                    )}
                    {addingSubTo === c.id ? (
                      <div className="flex items-center gap-1 mt-1" data-testid={`add-sub-input-${c.key}`}>
                        <input
                          autoFocus
                          className="vt-input py-1 text-xs"
                          placeholder="e.g. pedals"
                          value={subDraft}
                          onChange={(e) => setSubDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") { e.preventDefault(); addSubInline(c); }
                            if (e.key === "Escape") { setAddingSubTo(null); setSubDraft(""); }
                          }}
                          disabled={busy}
                        />
                        <button type="button" onClick={() => addSubInline(c)} disabled={busy} className="p-1 text-green-700 hover:bg-green-50" title="Save" data-testid={`save-sub-${c.key}`}><Check size={14} weight="bold" /></button>
                        <button type="button" onClick={() => { setAddingSubTo(null); setSubDraft(""); }} disabled={busy} className="p-1 text-neutral-500 hover:bg-neutral-100" title="Cancel"><X size={14} /></button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => { setAddingSubTo(c.id); setSubDraft(""); }}
                        className="text-[11px] text-[color:var(--vt-orange)] hover:underline font-bold inline-flex items-center gap-0.5 mt-1"
                        data-testid={`add-sub-btn-${c.key}`}
                      >
                        <Plus size={11} weight="bold" /> Add Sub
                      </button>
                    )}
                  </td>
                  <td className="p-3 text-right text-xs pt-4">{c.sort_order ?? 0}</td>
                  <td className="p-3 text-right pt-4">
                    {c.active ? (
                      <span className="inline-block bg-green-100 text-green-800 text-[10px] uppercase font-bold px-2 py-0.5">Active</span>
                    ) : (
                      <span className="inline-block bg-neutral-200 text-neutral-600 text-[10px] uppercase font-bold px-2 py-0.5">Hidden</span>
                    )}
                  </td>
                  <td className="p-3 text-right pt-4">
                    <button onClick={() => setMerging({ source: c })} className="p-2 hover:text-[color:var(--vt-orange)]" title="Merge into another category" data-testid={`merge-category-${c.key}`}><ArrowsMerge size={16} /></button>
                    <button onClick={() => setEditing(c)} className="p-2 hover:text-[color:var(--vt-orange)]" title="Edit main category (label, sort, reorder subs)" data-testid={`edit-category-${c.key}`}><PencilSimple size={16} /></button>
                    <button onClick={() => del(c)} className="p-2 hover:text-red-600" title="Delete main category" data-testid={`delete-category-${c.key}`}><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(editing || showNew) && (
        <CategoryModal
          category={editing}
          onClose={() => { setEditing(null); setShowNew(false); }}
          onSaved={load}
        />
      )}
      {merging && (
        <MergeModal
          source={merging.source}
          allCategories={items.filter((c) => c.id !== merging.source.id)}
          onClose={() => setMerging(null)}
          onDone={load}
        />
      )}
    </div>
  );
}

function MergeModal({ source, allCategories, onClose, onDone }) {
  const [targetKey, setTargetKey] = useState("");
  const [targetSub, setTargetSub] = useState("");
  const [saving, setSaving] = useState(false);
  const target = allCategories.find((c) => c.key === targetKey);
  const availableSubs = target?.subcategories || [];

  const merge = async () => {
    if (!targetKey) { toast.error("Pick a target category"); return; }
    if (!window.confirm(`Move all products from "${source.label}" into "${target?.label}${targetSub ? " → " + targetSub : ""}" and then delete "${source.label}"?`)) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/categories/${source.id}/merge`, {
        target_key: targetKey,
        target_subcategory: targetSub,
      });
      toast.success(`Merged — ${data.products_moved} product(s) moved to ${data.to_key}${data.to_subcategory ? " → " + data.to_subcategory : ""}`);
      onDone(); onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Merge failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="merge-modal">
      <div className="bg-white w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-neutral-200">
          <h2 className="font-display font-black text-2xl">Merge Category</h2>
          <button onClick={onClose} className="hover:text-[color:var(--vt-orange)]" data-testid="merge-modal-close"><X size={22} /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-neutral-600">
            Move every product currently in <strong>"{source.label}"</strong> into a chosen category (and optional subcategory),
            then delete <strong>"{source.label}"</strong>. Products keep all their other fields.
          </p>
          <div>
            <label className="overline text-neutral-500 block mb-1">Target Category*</label>
            <select className="vt-input" value={targetKey} onChange={(e) => { setTargetKey(e.target.value); setTargetSub(""); }} data-testid="merge-target-category">
              <option value="">— pick —</option>
              {allCategories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="overline text-neutral-500 block mb-1">
              Target Subcategory <span className="text-neutral-400 normal-case">(auto-added if new)</span>
            </label>
            <input className="vt-input" list={`merge-subs-${source.id}`} value={targetSub}
              placeholder="e.g. chains"
              onChange={(e) => setTargetSub(e.target.value)}
              disabled={!targetKey}
              data-testid="merge-target-subcategory" />
            <datalist id={`merge-subs-${source.id}`}>
              {availableSubs.map((s) => <option key={s} value={s} />)}
            </datalist>
            <p className="text-[11px] text-neutral-500 mt-1">
              Tip — enter the source's own name (<span className="font-mono">{source.key}</span>) to preserve it as a subcategory under the target.
            </p>
          </div>
          <div className="pt-2 flex gap-2 justify-end">
            <button onClick={onClose} className="vt-btn-outline text-xs">Cancel</button>
            <button onClick={merge} disabled={saving || !targetKey} className="vt-btn-orange text-xs" data-testid="merge-confirm">
              {saving ? "Merging…" : "Merge & Delete Source"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
